import React, {useState} from 'react';
import ReactPlayer from 'react-player';
import {OnProgressProps} from 'react-player/base';
import VideoPlayerControls from '../../../uiToolkit/VideoPlayerControls';
import {pauseControlsIcon, playControlsIcon} from '../../../assets/images';
import {
  LessonContentBase,
  LessonContentVideo,
  nodeIsASourceNode,
} from '../../../data/models/LessonContent/LessonContentBase';
// import {toast} from 'react-toastify';
// import {toastConfig} from '../../../uiToolkit/Toast/toastConfig';
import {truncateDurationTo3Decimals} from '../../../utils/getTimeTextFromSeconds';
import SubtitlesControl from './SubtitlesControl';

// const PIP_PLAYED_SECONDS_MARGIN = 3;
// const PIP_TICKS_UNTIL_REPORT = 10;

interface Props {
  source: string;
  startTime: number;
  endTime: number;
  onFinished: () => void;
  currentNode?: LessonContentBase;
  started?: boolean;
  showControls?: boolean;
  fitHeight?: boolean;
  onWidthCalculated: (width: number) => void;
  reference?: (ref: any) => void;
  setProgres: (prog: number) => void;
  hideSmallControls?: boolean;
}

const VideoPlayer = React.memo(
  ({
    startTime,
    endTime,
    onFinished,
    currentNode,
    started,
    showControls,
    fitHeight,
    onWidthCalculated,
    reference,
    setProgres,
    hideSmallControls,
  }: Props) => {
    const subtitlesUrl =
      !!currentNode && 'subtitlesUrl' in currentNode
        ? (currentNode?.subtitlesUrl as string)
        : '';
    const subtitlesAvailable = !!subtitlesUrl && subtitlesUrl !== '';
    const [newSource, setNewSource] = useState('');
    const [currentSource, setCurrentSource] = useState('');
    const [width, setWidth] = React.useState(0);
    const [height, setHeight] = React.useState(0);
    const [playing, setPlaying] = React.useState(false);
    const [duration, setDuration] = React.useState(-1);
    const [currentNodeId, setCurrentNodeId] = React.useState<string>();
    const [subtitlesEnabled, setSubtitlesEnabled] = useState(false);
    const [finishedPlaying, setFinishedPlaying] = useState(false);

    const containerRef = React.useRef<any>(null);
    const playerRef = React.useRef<ReactPlayer>(null);

    React.useEffect(() => {
      if (reference) {
        reference({pause, seekTo: externalSeekTo});
      }
    }, [currentNode, startTime, endTime]);

    React.useEffect(() => {
      if (
        currentNode &&
        nodeIsASourceNode(currentNode.ivNodeType) &&
        currentNode?.internalId !== currentNodeId &&
        started
      ) {
        if ((currentNode as LessonContentVideo).contentUrl !== currentSource) {
          setNewSource((currentNode as LessonContentVideo).contentUrl || '');
        } else {
          seekTo(startTime);
          setPlaying(true);
          setFinishedPlaying(false);
          setDuration(playerRef.current?.getInternalPlayer().duration);
        }
        setCurrentNodeId(currentNode.internalId);
      }
    }, [currentNode, started]);

    React.useEffect(() => {
      if (finishedPlaying) {
        setPlaying(false);
        setDuration(0);
        onFinished();
      }
    }, [finishedPlaying]);

    React.useEffect(() => {
      const video = containerRef.current?.querySelector('video');
      if (video && video.textTracks.length > 0) {
        const track = video.textTracks[0];
        track.mode = subtitlesEnabled ? 'showing' : 'disabled';
      }
    }, [subtitlesEnabled]);

    //Player controls functions
    const togglePlaying = () => {
      setPlaying(!playing);
    };

    const onProgress = (state: OnProgressProps) => {
      if (duration < 1) return;
      if (!nodeIsASourceNode(currentNode ? currentNode?.ivNodeType : -1)) {
        return;
      }
      let progress = 0;
      let finished = false;
      const playedSeconds = truncateDurationTo3Decimals(state.playedSeconds);
      if (startTime !== undefined && endTime !== undefined) {
        if (playedSeconds < startTime + 0.1) {
          playerRef.current?.seekTo(startTime + 0.1, 'seconds');
          progress = startTime + 0.1;
        } else if (playedSeconds > endTime - 0.1 || playedSeconds >= duration) {
          progress = endTime - 0.1;
          finished = true;
          if (playedSeconds > endTime - 0.1) {
            seekTo(endTime - 0.1);
          }
          if (playedSeconds >= duration) {
            seekTo(duration);
          }
        } else {
          progress = playedSeconds;
        }
      } else {
        progress = playedSeconds;
      }
      if (finished) {
        setFinishedPlaying(true);
      }
      if (setProgres) {
        setProgres(progress);
      }
    };

    const onEndedPlaying = () => {
      setFinishedPlaying(true);
    };

    const onDuration = (seconds: number) => {
      const truncatedDuration = truncateDurationTo3Decimals(seconds);
      setDuration(truncatedDuration);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const pause = () => {
      setPlaying(false);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const externalSeekTo = (second: number) => {
      seekTo(second);
    };

    const seekTo = (time: number) => {
      if (time < startTime) {
        playerRef.current?.seekTo(startTime + 0.1, 'seconds');
      } else if (time > endTime) {
        playerRef.current?.seekTo(endTime - 0.1, 'seconds');
      } else {
        playerRef.current?.seekTo(time, 'seconds');
      }
    };

    //UI Handlers
    const onReady = (player: ReactPlayer) => {
      if (playerRef && playerRef.current && !fitHeight) {
        const innerPlayer = player.getInternalPlayer();
        innerPlayer.style.borderRadius = '20px';
        innerPlayer.style.backgroundColor = 'transparent';
        innerPlayer.disablePictureInPicture = true;
      }
    };

    React.useEffect(() => {
      window.addEventListener('resize', setPlayerDimentions);
      return () => {
        window.removeEventListener('resize', setPlayerDimentions);
      };
    }, []);

    React.useLayoutEffect(() => {
      setPlayerDimentions();
    }, []);

    const setPlayerDimentions = () => {
      if (containerRef && containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
        onWidthCalculated(containerRef.current.offsetWidth);
        setHeight(containerRef.current.offsetHeight);
      }
    };

    const onNewSourceLoaded = () => {
      setCurrentSource(newSource);
      seekTo(startTime + 0.1);
      setPlaying(true);
      setFinishedPlaying(false);
    };

    const currentTime = playerRef.current
      ? playerRef.current.getCurrentTime()
      : 0;

    return (
      <div
        className="VideoPlaybackPlayerContainer"
        ref={containerRef}
        style={fitHeight ? {height: '100vh'} : {}}>
        <div className="VideoCover" />
        <ReactPlayer
          url={currentSource}
          playing={playing}
          ref={playerRef}
          width={width}
          height={fitHeight ? height : width / 1.77}
          config={{
            file: {
              tracks: subtitlesAvailable
                ? [
                    {
                      kind: 'subtitles',
                      src: subtitlesUrl,
                      srcLang: 'en',
                      label: 'English',
                    },
                  ]
                : [],
            },
          }}
          onReady={onReady}
          onProgress={onProgress}
          onDuration={onDuration}
          progressInterval={1}
          onEnded={onEndedPlaying}
          playsinline
          onError={e => {
            console.log(e);
          }}
          pip={false}
          onPause={() => {
            if (playing && playerRef) {
              playerRef.current?.getInternalPlayer().play();
            }
          }}
        />
        <ReactPlayer
          url={newSource}
          playing={false}
          style={{display: 'none'}}
          onReady={onNewSourceLoaded}
          config={{
            file: {
              tracks: subtitlesAvailable
                ? [
                    {
                      kind: 'subtitles',
                      src: subtitlesUrl,
                      srcLang: 'en',
                      label: 'English',
                    },
                  ]
                : [],
            },
          }}
        />
        {showControls && nodeIsASourceNode(currentNode?.ivNodeType) ? (
          <VideoPlayerControls
            playing={playing}
            duration={duration}
            togglePlaying={togglePlaying}
            currentTime={currentTime || 0}
            seekTo={seekTo}
            setPlaying={setPlaying}
            subtitlesAvailable={subtitlesAvailable}
            subtitlesEnabled={subtitlesEnabled}
            setSubtitlesEnabled={setSubtitlesEnabled}
          />
        ) : !hideSmallControls ? (
          <>
            <button className="SmallPlayButton" onClick={togglePlaying}>
              <img
                src={playing ? pauseControlsIcon : playControlsIcon}
                alt="play"
                className="Icon"
              />
            </button>
            <SubtitlesControl
              subtitlesAvailable={subtitlesAvailable}
              subtitlesEnabled={subtitlesEnabled}
              setSubtitlesEnabled={setSubtitlesEnabled}
              uiMode="big"
            />
          </>
        ) : null}
      </div>
    );
  },
);

export default VideoPlayer;
